/* V167.1 — FB Ad Campaigns Admin Tab
 *
 * Shows the full Manifest A/B/C × 6-language matrix (18 ads).
 * Each card has a "copy to clipboard" button so the admin can paste straight
 * into Facebook Ads Manager.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const LANGS = [
  { code: "sk", flag: "🇸🇰", name: "Slovak" },
  { code: "en", flag: "🇬🇧", name: "English" },
  { code: "de", flag: "🇩🇪", name: "German" },
  { code: "pl", flag: "🇵🇱", name: "Polish" },
  { code: "hu", flag: "🇭🇺", name: "Hungarian" },
  { code: "cs", flag: "🇨🇿", name: "Czech" },
];

const VARIANTS = [
  { id: "A", label: '"SEO tools are dead"', color: "border-red-500/40 bg-red-500/5" },
  { id: "B", label: '"Stop buying dashboards"', color: "border-amber-500/40 bg-amber-500/5" },
  { id: "C", label: '"Your store doesn\'t need another tool"', color: "border-emerald-500/40 bg-emerald-500/5" },
];

function adFullText(ad) {
  return [
    `HEADLINE: ${ad.headline}`,
    "",
    "PRIMARY TEXT:",
    ad.primary_text,
    "",
    `CTA BUTTON: ${ad.cta_button}`,
    "",
    `LANDING URL: ${ad.landing_url}`,
    ad.first_comment ? `\nFIRST COMMENT:\n${ad.first_comment}` : "",
  ].filter(Boolean).join("\n");
}

function AdCard({ ad }) {
  const [copied, setCopied] = useState("");
  const copy = (text, label) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(""), 1500);
    });
  };
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 space-y-3" data-testid={`fb-ad-${ad.variant}-${ad.language}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          Variant {ad.variant} · {ad.language.toUpperCase()}
        </div>
        <button
          onClick={() => copy(adFullText(ad), "all")}
          data-testid={`copy-all-${ad.variant}-${ad.language}`}
          className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-white/10 hover:bg-white/20 text-white rounded"
        >
          {copied === "all" ? "Skopírované!" : "Kopírovať celé"}
        </button>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-[10px] text-zinc-500 font-mono uppercase">Headline</div>
          <button
            onClick={() => copy(ad.headline, "headline")}
            className="text-[10px] text-emerald-400 hover:text-emerald-300"
          >
            {copied === "headline" ? "✓" : "copy"}
          </button>
        </div>
        <div className="font-black text-white text-base leading-tight">{ad.headline}</div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-[10px] text-zinc-500 font-mono uppercase">Primary text</div>
          <button
            onClick={() => copy(ad.primary_text, "primary")}
            className="text-[10px] text-emerald-400 hover:text-emerald-300"
          >
            {copied === "primary" ? "✓" : "copy"}
          </button>
        </div>
        <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-sans leading-relaxed bg-black/40 border border-zinc-900 rounded p-3 max-h-72 overflow-y-auto">
{ad.primary_text}
        </pre>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-[10px] text-zinc-500 font-mono uppercase mb-1">CTA Button</div>
          <div className="text-zinc-200 font-bold">{ad.cta_button}</div>
        </div>
        <div>
          <div className="text-[10px] text-zinc-500 font-mono uppercase mb-1">Landing URL</div>
          <a href={ad.landing_url} target="_blank" rel="noreferrer" className="text-emerald-400 hover:text-emerald-300 truncate block font-mono text-[10px]">
            {ad.landing_url}
          </a>
        </div>
      </div>

      {ad.first_comment && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[10px] text-zinc-500 font-mono uppercase">First comment (pin under ad)</div>
            <button
              onClick={() => copy(ad.first_comment, "comment")}
              className="text-[10px] text-emerald-400 hover:text-emerald-300"
            >
              {copied === "comment" ? "✓" : "copy"}
            </button>
          </div>
          <div className="text-xs text-zinc-300 italic bg-zinc-900/50 border-l-2 border-zinc-700 pl-3 py-2 rounded-r">
            {ad.first_comment}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminFbAdCampaignsTab() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(null);  // V218.12 — {total, completed, error_count}
  const [err, setErr] = useState("");
  const [filterVariant, setFilterVariant] = useState("all");
  const [filterLang, setFilterLang] = useState("all");

  const load = () => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) { setErr("Admin token chýba."); return; }
    setLoading(true);
    axios.get(`${API}/fb-ads/campaign`, { headers: { "X-Admin-Token": token } })
      .then((r) => setAds(r.data.ads || []))
      .catch((e) => setErr(e.response?.data?.detail || "load_failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const generate = async (overwrite) => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) return;
    if (overwrite && !window.confirm("Pregenerovať všetkých 18 reklám? Existujúce sa prepíšu.")) return;
    setGenerating(true);
    setProgress(null);
    setErr("");
    try {
      // V218.12 — endpoint now returns 202 immediately with a campaign_id.
      // We then poll /campaign-status until it's "complete".
      const r = await axios.post(`${API}/fb-ads/generate-campaign`, { overwrite },
        { headers: { "X-Admin-Token": token }, timeout: 30000 });
      const campaignId = r.data.campaign_id;
      const total = r.data.total || 18;
      setProgress({ total, completed: 0, error_count: 0 });

      // Poll every 3 sec — Claude burst usually finishes in 30-90s.
      let attempts = 0;
      while (attempts < 120) { // safety cap: 6 min
        await new Promise((s) => setTimeout(s, 3000));
        attempts += 1;
        try {
          const st = await axios.get(
            `${API}/fb-ads/campaign-status/${campaignId}`,
            { headers: { "X-Admin-Token": token }, timeout: 10000 }
          );
          setProgress({
            total: st.data.total,
            completed: st.data.completed,
            error_count: st.data.error_count,
          });
          if (st.data.status === "complete") {
            if (st.data.error_count > 0) {
              setErr(
                `Vygenerovaných ${st.data.completed - st.data.error_count}/${st.data.total}. `
                + `Chýba ${st.data.error_count}. Možno došiel LLM kredit — pozri Profile → Universal Key.`
              );
            }
            await load();
            return;
          }
        } catch (pollErr) {
          // Transient poll error — keep trying.
          console.warn("poll error", pollErr.message);
        }
      }
      setErr("Generácia trvá nezvyčajne dlho. Skontroluj logy alebo skús neskôr.");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      if (msg.includes("Budget") || msg.includes("budget")) {
        setErr("Emergent LLM kredit minutý. Choď do Profile → Universal Key → Add Balance.");
      } else {
        setErr(msg);
      }
    } finally {
      setGenerating(false);
    }
  };

  const filtered = ads.filter((a) =>
    (filterVariant === "all" || a.variant === filterVariant) &&
    (filterLang === "all" || a.language === filterLang)
  );

  return (
    <div className="p-3 sm:p-6 space-y-5" data-testid="admin-fb-ad-campaigns-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-white">📣 FB Reklamy — Manifest kampaň</h2>
          <p className="text-zinc-400 text-sm mt-1">V167.1 — Claude Sonnet 4.5 · 3 varianty × 6 jazykov = 18 reklám</p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="fb-ads-refresh"
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs rounded hover:bg-zinc-800 disabled:opacity-50"
          >
            ↻ Obnoviť
          </button>
          <button
            data-testid="fb-ads-generate"
            onClick={() => generate(false)}
            disabled={generating}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded disabled:opacity-50"
          >
            {generating ? "Generujem…" : "Dogeneruj chýbajúce"}
          </button>
          <button
            data-testid="fb-ads-regenerate"
            onClick={() => generate(true)}
            disabled={generating}
            className="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded disabled:opacity-50"
          >
            {generating ? "Generujem…" : "Pregenerovať všetky"}
          </button>
        </div>
      </div>

      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 rounded text-sm" data-testid="fb-ads-error">
          {err}
        </div>
      )}

      {/* V218.12 — Live progress bar while background generation runs */}
      {generating && progress && (
        <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-lg p-3" data-testid="fb-ads-progress">
          <div className="flex items-center justify-between text-xs font-mono mb-2">
            <span className="text-emerald-300">⚡ Generujem na pozadí…</span>
            <span className="text-zinc-400">
              {progress.completed}/{progress.total}
              {progress.error_count > 0 && (
                <span className="text-rose-300 ml-2">· {progress.error_count} chýb</span>
              )}
            </span>
          </div>
          <div className="h-2 bg-zinc-900 rounded overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${Math.round((progress.completed / Math.max(1, progress.total)) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-zinc-500 font-mono uppercase tracking-wider">Variant:</span>
        <button onClick={() => setFilterVariant("all")} className={`px-2 py-1 rounded ${filterVariant === "all" ? "bg-white text-black font-bold" : "bg-zinc-900 text-zinc-400"}`}>All</button>
        {VARIANTS.map((v) => (
          <button key={v.id} onClick={() => setFilterVariant(v.id)} className={`px-2 py-1 rounded ${filterVariant === v.id ? "bg-white text-black font-bold" : "bg-zinc-900 text-zinc-400"}`}>{v.id}</button>
        ))}
        <span className="text-zinc-500 font-mono uppercase tracking-wider ml-3">Lang:</span>
        <button onClick={() => setFilterLang("all")} className={`px-2 py-1 rounded ${filterLang === "all" ? "bg-white text-black font-bold" : "bg-zinc-900 text-zinc-400"}`}>All</button>
        {LANGS.map((l) => (
          <button key={l.code} onClick={() => setFilterLang(l.code)} className={`px-2 py-1 rounded ${filterLang === l.code ? "bg-white text-black font-bold" : "bg-zinc-900 text-zinc-400"}`}>{l.flag} {l.code.toUpperCase()}</button>
        ))}
        <span className="text-zinc-500 text-[10px] ml-auto font-mono">{filtered.length} / {ads.length}</span>
      </div>

      {/* Grid grouped by variant */}
      {VARIANTS.filter((v) => filterVariant === "all" || filterVariant === v.id).map((v) => {
        const rows = filtered.filter((a) => a.variant === v.id);
        if (rows.length === 0 && filterVariant !== "all" && filterVariant !== v.id) return null;
        return (
          <div key={v.id} className="space-y-3">
            <div className={`px-4 py-2 rounded-lg border ${v.color}`}>
              <div className="text-xs font-black text-white">Variant {v.id} — {v.label}</div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {rows.map((ad) => <AdCard key={`${ad.variant}-${ad.language}`} ad={ad}/>)}
              {rows.length === 0 && (
                <div className="text-zinc-500 text-sm col-span-2 px-4 py-6 border border-dashed border-zinc-800 rounded-lg">
                  Žiadne reklamy pre variant {v.id}. Klikni „Dogeneruj chýbajúce".
                </div>
              )}
            </div>
          </div>
        );
      })}

      {ads.length === 0 && !loading && (
        <div className="text-center py-12 border border-dashed border-zinc-800 rounded-xl">
          <p className="text-zinc-400 mb-3">Žiadne reklamy ešte nie sú vygenerované.</p>
          <button onClick={() => generate(false)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold rounded">
            Vygenerovať 18 reklám teraz
          </button>
        </div>
      )}
    </div>
  );
}
